import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  DashboardSummary,
  HistoryDataPoint,
  PrStatusBreakdown,
  TaskStatusBreakdown,
} from '@resolve-ai/shared';

import { useAuth } from '@/contexts/AuthContext.tsx';
import { dashboardApi } from '../lib/dashboard-api';

interface DashboardPageData {
  summary: DashboardSummary;
  taskBreakdown: TaskStatusBreakdown;
  prBreakdown: PrStatusBreakdown;
  monthlyHistory: HistoryDataPoint[];
}

const DASHBOARD_HISTORY_PERIOD = 'month' as const;
const DASHBOARD_PAGE_QUERY_KEY = ['dashboard-page', DASHBOARD_HISTORY_PERIOD] as const;

export function useDashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const dashboardQuery = useQuery<DashboardPageData>({
    queryKey: DASHBOARD_PAGE_QUERY_KEY,
    queryFn: async () => {
      const [summary, taskBreakdown, prBreakdown, monthlyHistory] = await Promise.all([
        dashboardApi.getSummary(),
        dashboardApi.getTaskBreakdown(),
        dashboardApi.getPrBreakdown(),
        dashboardApi.getHistory(DASHBOARD_HISTORY_PERIOD),
      ]);

      return {
        summary,
        taskBreakdown,
        prBreakdown,
        monthlyHistory,
      };
    },
  });

  const summary = dashboardQuery.data?.summary ?? null;
  const taskBreakdown = dashboardQuery.data?.taskBreakdown ?? null;
  const prBreakdown = dashboardQuery.data?.prBreakdown ?? null;
  const monthlyHistory = dashboardQuery.data?.monthlyHistory ?? [];

  const bugStatus = useMemo(
    () => ({
      resolved: taskBreakdown?.completed ?? 0,
      inReview: prBreakdown?.open ?? 0,
      rejected: prBreakdown?.closed ?? 0,
      accepted: prBreakdown?.merged ?? 0,
      total: taskBreakdown?.total ?? 0,
    }),
    [prBreakdown, taskBreakdown]
  );

  return {
    query: {
      isLoading: dashboardQuery.isLoading,
      hasError: dashboardQuery.isError,
      error: dashboardQuery.error,
      refetch: dashboardQuery.refetch,
    },
    isAdmin,
    summary,
    taskBreakdown,
    prBreakdown,
    bugStatus,
    monthlyHistory,
  };
}
